import { getSessionAuthentication, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T | null> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

// Types

export interface User {
    id: number;
    firstName: string;
    lastName: string;
    username: string;
    password?: string;
    usernamePos?: string;
    employeeNumber?: string;
    socialSecurityNumber?: string;
    startDate?: string;
    isActive: boolean;
    isAllowedToViewCommonReports?: boolean;
    salaryType?: string;
    role: Role | null;
    addresses: Address[];
    contactInfos: ContactInfo[];
    locations: Location[];
    brands: Brand[];
}

export interface Role {
    id: number;
    roleName: string;
}

export interface AddressType {
    id: number;
    typeName: string;
}

export interface Address {
    id?: number;
    addressType: AddressType;
    addressTypeId?: number;
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    postalCode?: string;
    city?: string;
    country?: string;
    [key: string]: unknown;
}

export interface ContactInfoType {
    id: number;
    typeName: string;
    systemTypeName?: string;
}

export interface ContactInfo {
    id?: number;
    contactInfoType: ContactInfoType;
    value?: string;
    [key: string]: unknown;
}

export interface Location {
    id: number;
    locationNumber?: number;
    locationName: string;
}

export interface Brand {
    id: number;
    brandCode: string;
    brandDescription: string;
}

// API calls

export async function getUser(userId: number): Promise<User> {
    const result = await rms2Fetch<User>(`users/${userId}/detail`);
    if (!result) throw new Error('Empty response for getUser');
    return result;
}

export async function createUser(user: Partial<User>): Promise<User> {
    const result = await rms2Fetch<User>('users', {
        method: 'POST',
        body: JSON.stringify({
            firstName: user.firstName,
            lastName: user.lastName,
            username: user.username,
            password: user.password,
            usernamePos: user.usernamePos,
            employeeNumber: user.employeeNumber,
            socialSecurityNumber: user.socialSecurityNumber,
            startDate: user.startDate,
            isActive: user.isActive,
            salaryType: user.salaryType,
            roleId: user.role?.id ?? null,
            locationIds: user.locations?.map((l) => l.id) ?? [],
            brandIds: user.brands?.map((b) => b.id) ?? [],
            addresses: user.addresses?.map((a) => ({
                addressTypeId: a.addressType?.id ?? a.addressTypeId,
                addressLine1: a.addressLine1,
                addressLine2: a.addressLine2,
                addressLine3: a.addressLine3,
                postalCode: a.postalCode,
                city: a.city,
                country: a.country
            })) ?? [],
            contactInfos: user.contactInfos?.map((c) => ({
                contactInfoTypeId: c.contactInfoType?.id,
                value: c.value
            })) ?? []
        })
    });
    if (!result) throw new Error('Empty response for createUser');
    return result;
}

export async function updateUser(userId: number, user: Partial<User>): Promise<User> {
    const result = await rms2Fetch<User>(`users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({
            firstName: user.firstName,
            lastName: user.lastName,
            username: user.username,
            password: user.password,
            usernamePos: user.usernamePos,
            employeeNumber: user.employeeNumber,
            socialSecurityNumber: user.socialSecurityNumber,
            startDate: user.startDate,
            isActive: user.isActive,
            salaryType: user.salaryType,
            roleId: user.role?.id ?? null,
            locationIds: user.locations?.map((l) => l.id) ?? [],
            brandIds: user.brands?.map((b) => b.id) ?? [],
            addresses: user.addresses?.map((a) => ({
                addressTypeId: a.addressType?.id ?? a.addressTypeId,
                addressLine1: a.addressLine1,
                addressLine2: a.addressLine2,
                addressLine3: a.addressLine3,
                postalCode: a.postalCode,
                city: a.city,
                country: a.country
            })) ?? [],
            contactInfos: user.contactInfos?.map((c) => ({
                contactInfoTypeId: c.contactInfoType?.id,
                value: c.value
            })) ?? []
        })
    });
    if (!result) throw new Error('Empty response for updateUser');
    return result;
}

export async function getAddressTypes(): Promise<AddressType[]> {
    const response = await rms2Fetch<{ addressTypes: AddressType[] }>('addresstypes');
    return response?.addressTypes ?? [];
}

export async function getContactInfoTypes(): Promise<ContactInfoType[]> {
    const response = await rms2Fetch<{ contactInfoTypes: ContactInfoType[] }>('users/contactinfotypes');
    return response?.contactInfoTypes ?? [];
}

export async function getValidRoles(): Promise<Role[]> {
    const response = await rms2Fetch<{ roles: Role[] }>('users/validuserroles');
    return response?.roles ?? [];
}

export async function getLocations(): Promise<Location[]> {
    const response = await rms2Fetch<{ locations: Location[] }>('users/locations');
    return response?.locations ?? [];
}

export async function getBrands(): Promise<Brand[]> {
    const response = await rms2Fetch<{ brands: Brand[] }>('users/brands');
    return response?.brands ?? [];
}
